import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    ArrowLeft,
    Loader2,
    Megaphone,
    Send,
    Users,
    User,
    UsersRound,
    History,
    AlertCircle,
    CheckCircle2,
    XCircle,
    ChevronRight,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { STATUS_META, STATUS_ORDER } from "@/lib/permissions";
import { SPORT_LABELS } from "@/lib/athleteTaxonomy";
import SportsPicker from "@/components/SportsPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TARGETS = [
    { value: "parents", label: "Parents", icon: User },
    { value: "athletes", label: "Children", icon: UsersRound },
    { value: "both", label: "Both", icon: Users },
];

export default function CoachAnnouncements() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/", { replace: true }); return; }
        if (user.role !== "owner" && !user.permissions?.manage_announcements) {
            toast.error("You don't have access to announcements");
            navigate("/coach", { replace: true });
        }
    }, [loading, user, navigate]);

    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [activeId, setActiveId] = useState(null);
    const [activeDetail, setActiveDetail] = useState(null);

    const [statuses, setStatuses] = useState([]);  // empty = all
    const [sports, setSports] = useState([]);       // empty = all sports
    const [target, setTarget] = useState("parents");
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [previewCount, setPreviewCount] = useState(null);
    const [previewBy, setPreviewBy] = useState({ parent: 0, athlete: 0 });
    const [previewing, setPreviewing] = useState(false);
    const [sending, setSending] = useState(false);

    const refreshHistory = async () => {
        setHistoryLoading(true);
        try {
            const r = await axios.get(`${API}/coach/announcements`, { withCredentials: true });
            setHistory(r.data || []);
        } catch (e) {
            toast.error("Couldn't load history", { description: e?.response?.data?.detail });
        } finally {
            setHistoryLoading(false);
        }
    };
    useEffect(() => { if (user?.role === "owner" || user?.permissions?.manage_announcements) refreshHistory(); }, [user]);

    const openDetail = async (id) => {
        setActiveId(id);
        try {
            const r = await axios.get(`${API}/coach/announcements/${id}`, { withCredentials: true });
            setActiveDetail(r.data);
        } catch (e) {
            toast.error("Couldn't load detail", { description: e?.response?.data?.detail });
        }
    };

    const toggleStatus = (s) => {
        setStatuses((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
        setPreviewCount(null);
    };

    const clearSports = () => { setSports([]); setPreviewCount(null); };

    const previewAudience = async () => {
        if (!subject.trim() || !body.trim()) {
            toast.error("Subject and body required to preview");
            return;
        }
        setPreviewing(true);
        try {
            const r = await axios.post(
                `${API}/coach/announcements/preview`,
                { subject, body, statuses, sports, target },
                { withCredentials: true },
            );
            setPreviewCount(r.data.recipient_count);
            setPreviewBy(r.data.by_type || { parent: 0, athlete: 0 });
        } catch (e) {
            toast.error("Couldn't preview", { description: e?.response?.data?.detail });
        } finally {
            setPreviewing(false);
        }
    };

    const send = async (testOnly) => {
        if (!subject.trim() || !body.trim()) {
            toast.error("Subject and body are required");
            return;
        }
        if (!testOnly && previewCount === null) {
            toast.error("Preview the audience before sending");
            return;
        }
        if (!testOnly && previewCount === 0) {
            toast.error("No recipients match this audience");
            return;
        }
        if (!testOnly && !window.confirm(`Send to ${previewCount} recipient(s)?`)) return;
        setSending(true);
        try {
            const r = await axios.post(
                `${API}/coach/announcements`,
                { subject, body, statuses, sports, target, test_only: !!testOnly },
                { withCredentials: true },
            );
            toast.success(
                testOnly ? "Test sent to your inbox" : `Sent ${r.data.sent_count} of ${r.data.recipient_count}`,
                r.data.failed_count ? { description: `${r.data.failed_count} failed` } : undefined,
            );
            if (!testOnly) {
                setSubject("");
                setBody("");
                setPreviewCount(null);
                refreshHistory();
            }
        } catch (e) {
            toast.error("Send failed", { description: e?.response?.data?.detail });
        } finally {
            setSending(false);
        }
    };

    if (loading || !user) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-brand-red" />
            </div>
        );
    }

    return (
        <div data-testid="coach-announcements" className="min-h-screen bg-black text-white">
            <div className="max-w-7xl mx-auto px-4 pt-28 pb-10">
                <div className="flex items-center gap-3 mb-6">
                    <Link to="/coach" className="text-zinc-200 hover:text-white inline-flex items-center gap-1">
                        <ArrowLeft className="w-4 h-4" /> Coach console
                    </Link>
                </div>

                <div className="flex items-end gap-3 mb-8">
                    <div>
                        <h1 className="text-4xl sm:text-5xl font-black tracking-tight flex items-center gap-3">
                            <Megaphone className="w-8 h-8 text-brand-red" />
                            Announcements
                        </h1>
                        <p className="mt-2 text-zinc-200">
                            Send a broadcast email to a filtered slice of your roster. Pick a status filter, choose who receives it, preview the count, then send.
                        </p>
                    </div>
                </div>

                <div className="grid lg:grid-cols-12 gap-6">
                    {/* Compose */}
                    <div className="lg:col-span-7 border border-zinc-800 rounded-2xl bg-zinc-950/50 p-6 space-y-5">
                        <div>
                            <label className="text-xs uppercase tracking-wider text-zinc-300 block mb-2">Audience — status</label>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={() => { setStatuses([]); setPreviewCount(null); }}
                                    className={`text-xs px-3 py-1.5 rounded-full border transition ${
                                        statuses.length === 0
                                            ? "border-brand-red text-brand-red bg-brand-red/10"
                                            : "border-zinc-700 text-zinc-200 hover:border-zinc-500"
                                    }`}
                                >
                                    All
                                </button>
                                {STATUS_ORDER.map((s) => {
                                    const meta = STATUS_META[s];
                                    const selected = statuses.includes(s);
                                    return (
                                        <button
                                            key={s}
                                            type="button"
                                            data-testid={`audience-status-${s}`}
                                            onClick={() => toggleStatus(s)}
                                            className={`text-xs px-3 py-1.5 rounded-full border transition ${
                                                selected
                                                    ? meta.className
                                                    : "border-zinc-700 text-zinc-200 hover:border-zinc-500"
                                            }`}
                                        >
                                            {meta.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div>
                            <label className="text-xs uppercase tracking-wider text-zinc-300 block mb-2">Audience — recipient</label>
                            <div className="grid grid-cols-3 gap-2">
                                {TARGETS.map((t) => {
                                    const Icon = t.icon;
                                    const selected = target === t.value;
                                    return (
                                        <button
                                            key={t.value}
                                            type="button"
                                            data-testid={`audience-target-${t.value}`}
                                            onClick={() => { setTarget(t.value); setPreviewCount(null); }}
                                            className={`inline-flex flex-col items-center gap-1.5 py-3 border rounded-lg text-[11px] tracking-wider uppercase transition ${
                                                selected
                                                    ? "border-brand-red text-brand-red bg-brand-red/10"
                                                    : "border-zinc-800 text-zinc-300 hover:border-zinc-600"
                                            }`}
                                        >
                                            <Icon className="w-4 h-4" />
                                            {t.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-[11px] text-zinc-300 mt-2 flex items-start gap-1.5">
                                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                Children are only emailed if they have an athlete email on file in their profile.
                            </p>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-xs uppercase tracking-wider text-zinc-300">
                                    Audience — sport{sports.length > 0 && ` · ${sports.length} selected`}
                                </label>
                                {sports.length > 0 && (
                                    <button
                                        type="button"
                                        data-testid="audience-sport-clear"
                                        onClick={clearSports}
                                        className="text-[10px] tracking-wider uppercase text-zinc-300 hover:text-brand-red"
                                    >
                                        Clear · all sports
                                    </button>
                                )}
                            </div>
                            {sports.length === 0 && (
                                <p className="text-[11px] text-zinc-400 mb-2">
                                    Leave empty to include every roster — or type sports below (e.g., "sof…" → Softball) to email only those athletes (and their parents, when "Both" / "Parents" is the target).
                                </p>
                            )}
                            <SportsPicker
                                value={sports}
                                onChange={(next) => { setSports(next); setPreviewCount(null); }}
                                testidPrefix="audience-sport"
                                allowOther={false}
                                placeholder="Type a sport to add…"
                            />
                        </div>

                        <div>
                            <label className="text-xs uppercase tracking-wider text-zinc-300 block mb-2">Subject</label>
                            <input
                                data-testid="announcement-subject"
                                value={subject}
                                onChange={(e) => { setSubject(e.target.value); setPreviewCount(null); }}
                                placeholder="e.g. New summer schedule is live"
                                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-red"
                            />
                        </div>

                        <div>
                            <label className="text-xs uppercase tracking-wider text-zinc-300 block mb-2">Body</label>
                            <textarea
                                data-testid="announcement-body"
                                rows={8}
                                value={body}
                                onChange={(e) => { setBody(e.target.value); setPreviewCount(null); }}
                                placeholder="Hey team — quick update…"
                                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-red font-mono"
                            />
                            <p className="text-[11px] text-zinc-300 mt-1">Plain text or basic HTML. Line breaks are preserved.</p>
                        </div>

                        <div className="flex flex-wrap items-center gap-3 pt-2">
                            <button
                                type="button"
                                data-testid="announcement-preview"
                                onClick={previewAudience}
                                disabled={previewing}
                                className="btn-ghost-tactical"
                            >
                                {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Preview audience"}
                            </button>
                            {previewCount !== null && (
                                <div data-testid="announcement-preview-result" className="text-sm text-zinc-300">
                                    <strong className="text-white">{previewCount}</strong> recipient(s)
                                    {previewCount > 0 && (
                                        <span className="text-zinc-300"> ({previewBy.parent} parent · {previewBy.athlete} athlete)</span>
                                    )}
                                </div>
                            )}
                            <div className="flex-1" />
                            <button
                                type="button"
                                data-testid="announcement-test"
                                onClick={() => send(true)}
                                disabled={sending || !subject.trim() || !body.trim()}
                                className="btn-ghost-tactical"
                            >
                                Send test to me
                            </button>
                            <button
                                type="button"
                                data-testid="announcement-send"
                                onClick={() => send(false)}
                                disabled={sending || previewCount === null || previewCount === 0}
                                className="btn-tactical disabled:opacity-50"
                            >
                                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                Send
                            </button>
                        </div>
                    </div>

                    {/* History */}
                    <div className="lg:col-span-5 border border-zinc-800 rounded-2xl bg-zinc-950/50">
                        <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-2">
                            <History className="w-4 h-4 text-zinc-200" />
                            <h2 className="text-lg font-semibold">History</h2>
                            <span className="ml-auto text-xs text-zinc-300">{history.length}</span>
                        </div>
                        <div data-testid="announcement-history" className="divide-y divide-zinc-800 max-h-[60vh] overflow-y-auto">
                            {historyLoading ? (
                                <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-brand-red" /></div>
                            ) : history.length === 0 ? (
                                <p className="p-6 text-sm text-zinc-300">No announcements sent yet.</p>
                            ) : history.map((a) => (
                                <button
                                    key={a.announcement_id}
                                    type="button"
                                    data-testid={`history-${a.announcement_id}`}
                                    onClick={() => openDetail(a.announcement_id)}
                                    className="w-full text-left px-5 py-4 hover:bg-zinc-900/60 transition flex items-center gap-3"
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="font-semibold truncate">{a.subject}</div>
                                        <div className="text-xs text-zinc-300 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                                            <span>{new Date(a.created_at).toLocaleString()}</span>
                                            <span>·</span>
                                            <span className="inline-flex items-center gap-1 text-brand-navy-300">
                                                <CheckCircle2 className="w-3 h-3" /> {a.sent_count} sent
                                            </span>
                                            {a.failed_count > 0 && (
                                                <span className="inline-flex items-center gap-1 text-brand-red">
                                                    <XCircle className="w-3 h-3" /> {a.failed_count} failed
                                                </span>
                                            )}
                                            {a.test_only && <span className="text-amber-400">· TEST</span>}
                                        </div>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-zinc-200" />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {activeId && activeDetail && (
                <DetailModal
                    detail={activeDetail}
                    onResend={(d) => {
                        setSubject(d.subject);
                        setBody(d.body);
                        setStatuses(d.statuses || []);
                        setSports(d.sports || []);
                        setTarget(d.target);
                        setPreviewCount(null);
                        setActiveId(null);
                        setActiveDetail(null);
                        toast.info("Loaded into composer — preview & send when ready");
                    }}
                    onClose={() => { setActiveId(null); setActiveDetail(null); }}
                />
            )}
        </div>
    );
}

function DetailModal({ detail, onResend, onClose }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
                    <h3 className="text-lg font-semibold truncate pr-4">{detail.subject}</h3>
                    <button type="button" onClick={onClose} className="text-zinc-200 hover:text-white text-xl">×</button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="text-xs text-zinc-300">
                        Sent {new Date(detail.created_at).toLocaleString()} ·
                        {" "}<span className="text-brand-navy-300">{detail.sent_count} sent</span>
                        {detail.failed_count > 0 && <> · <span className="text-brand-red">{detail.failed_count} failed</span></>} ·
                        {" "}target: {detail.target}
                        {detail.statuses?.length ? ` · status: ${detail.statuses.join(", ")}` : " · status: all"}
                        {detail.sports?.length ? ` · sports: ${detail.sports.map((s) => SPORT_LABELS[s] || s).join(", ")}` : ""}
                    </div>
                    <div className="border border-zinc-800 rounded-lg p-4 bg-zinc-900/40">
                        <div className="text-xs uppercase tracking-wider text-zinc-300 mb-2">Body</div>
                        <div className="text-sm text-zinc-200 whitespace-pre-wrap">{detail.body}</div>
                    </div>
                    {detail.recipients?.length > 0 && (
                        <div className="border border-zinc-800 rounded-lg overflow-hidden">
                            <div className="text-xs uppercase tracking-wider text-zinc-300 px-4 py-2 border-b border-zinc-800">Recipients</div>
                            <div className="max-h-64 overflow-y-auto divide-y divide-zinc-800">
                                {detail.recipients.map((r, i) => (
                                    <div key={i} className="px-4 py-2 text-sm flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="truncate">{r.name}</div>
                                            <div className="text-xs text-zinc-300 truncate">{r.to} · {r.type}</div>
                                        </div>
                                        {r.status === "sent" ? (
                                            <span className="text-xs text-brand-navy-300">sent</span>
                                        ) : (
                                            <span className="text-xs text-brand-red" title={r.error}>failed</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="btn-ghost-tactical">Close</button>
                        <button type="button" data-testid="announcement-resend" onClick={() => onResend(detail)} className="btn-tactical">
                            Re-use as new
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
